// @ts-nocheck
import {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi, usePlayMakersConfig} from "../PlayMakersProvider";
import {useComment} from "./useComment";
import {usePagination} from "./usePagination";

interface UseSubmissionValue {
  submission: any;
  fetched: boolean;
  isPending: boolean | null;
  uploadPendingAfterTimeout: boolean;
  push: (updatedSubmission?: any) => Promise<void>;
  pushSubmission: (updatedSubmission?: any) => Promise<void>;
  deleteSubmission: (submissionId?: any) => Promise<void>;
  setDescription: (description: string) => void;
  setFiles: (files: any) => void;
  setName: (name: string) => void;
  setPostTitle: (postTitle: string) => void;
  setState: (state: string) => void;
  setId: (id: string) => void;
  setSchemaId: (schemaId: string) => void;
  approve: () => void;
  approveSubmission: () => void;
  reject: () => void;
  rejectSubmission: () => void;
  reset: () => void;
  refetch: (quiet: boolean) => void;
}

interface UseSubmissionsValue {
  submissions: Array<any>;
  approveAll: () => void;
  refresh: (quiet: boolean) => void;
  rejectAll: () => void;
  fetchMore: (refresh?: boolean) => Promise<void>;
  fetched: boolean;
  hasMore: boolean;
  currentPage: number;
}

interface SubmissionParams {
  projectId?: string;
  schemaId?: string;
  state?: string | string[];
  ownerId?: string;
  limit?: number;
  page?: number;
}

export const useSubmission = (initId: string | null, checkForPending: boolean = false ): UseSubmissionValue => {
  const {createSubmission, getSubmission, updateSubmission, deleteSubmission: deleteSubmissionFromApi} = useApi();
  const {createApproveComment, createRejectComment} = useComment();
  const [id, setIdWithoutRefetch] = useState<any>(initId);
  const [refetchSubmission, setRefetchSubmission] = useState<boolean>(false);
  const [submission, setSubmission] = useState<any>({});
  const [submissionFiles, setSubmissionFiles] = useState<any>([]);
  const [fetched, setFetched] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean | null>(null);
  const [uploadPendingAfterTimeout, setUploadPendingAfterTimeout] = useState<boolean>(false);

  const {SUBMISSION_UPLOAD_POLL_PERIOD, SUBMISSION_UPLOAD_TIMEOUT} = usePlayMakersConfig();

  // Setter functions update internal state, not push to backend
  const setDefaults = () => {
    setIdWithoutRefetch(initId);
    setSubmission({
      description: "",
      name: "",
      postTitle: "",
      schemaId: "",
      state: ""
    });
  };

  const setId = (id: string) => {
    setIdWithoutRefetch(id);
    setRefetchSubmission(prev => !prev);
  };

  // Fetch the submission from the backend
  const fetchSubmission = async(quiet = false) => {
    if(!id) return setDefaults();
    try {
      setFetched(quiet);
      const submission = await getSubmission(id);
      setIsPending(submission.state?.startsWith("pending"));
      setSubmission(submission);
      setFetched(true);
      return submission;
    } catch (error) {
      console.error(`Error fetching submission ${id}`, error);
    }
  };

  // Initial fetch on mount or when refetchSubmission changes
  useEffect(() => {
    fetchSubmission();
  }, [id, refetchSubmission]);

  // Handle polling and timeout
  useEffect(() => {
    const poll = async(firstCheckDate=Date.now()) => {
      const submission = await fetchSubmission();
      if(!submission.state?.startsWith("pending")) return;

      const timeSinceFirstCheck = Date.now() - firstCheckDate;
      if(SUBMISSION_UPLOAD_TIMEOUT && timeSinceFirstCheck > SUBMISSION_UPLOAD_TIMEOUT)
        setUploadPendingAfterTimeout(true);
      else
        setTimeout(() => poll(firstCheckDate), SUBMISSION_UPLOAD_POLL_PERIOD);
    };

    if(checkForPending && isPending)
      poll();


  }, [isPending]);


  // Approve the submission: update state and push
  const approveSubmission = async(commentBody?: string) => {
    setSubmission((prev:any) => ({...prev, state: "approved"}));
    const updatedSubmission = await pushSubmission({state: "approved"});
    if(!updatedSubmission) setRefetchSubmission(prev => !prev);
    else if(commentBody !== undefined) await createApproveComment(submission.id, commentBody);
  };

  // Reject the submission: update state and push
  const rejectSubmission = async(commentBody?: string) => {
    setSubmission((prev:any) => ({...prev, state: "rejected"}));
    const updatedSubmission = await pushSubmission({state: "rejected"});
    if(!updatedSubmission) setRefetchSubmission(prev => !prev);
    else if(commentBody !== undefined) await createRejectComment(submission.id, commentBody);
  };

  // Delete the submission
  const deleteSubmission = async(submissionId?: string) => {
    try {
      await deleteSubmissionFromApi(submissionId);
    } catch (error) {
      console.error(`Error deleting submission ${submissionId}:`, error);
    }
  };

  // Push the submission to the backend
  const pushSubmission = async(updatedSubmission: any, refetch: boolean = false) => {
    updatedSubmission = {...submission, ...updatedSubmission};
    if(!updatedSubmission) {
      console.error("No submission to push");
      return;
    }

    if(
      !updatedSubmission.name ||
      !updatedSubmission.postTitle ||
      !updatedSubmission.schemaId) {
      console.error(`Missing required parameters for ${submission.id || ""} submission. ` +
        `${updatedSubmission.name ? "" : "Name is missing. "}` +
        `${updatedSubmission.schemaId ? "" : "schemaId is missing. "}`);
      return;
    }

    const params: any = {
      description: updatedSubmission.description || "",
      name: updatedSubmission.name,
      postTitle: updatedSubmission.postTitle,
      schemaId: updatedSubmission.schemaId,
      state: updatedSubmission.state || "draft"
    };

    try {
      let submission;
      if(id)
        submission = await updateSubmission(id, params, submissionFiles);
      else
        submission = await createSubmission(params, submissionFiles);
      if(!submission) throw new Error("Submission not created");
      setSubmission(submission);
      setIdWithoutRefetch(submission.id);
      if(refetch)  setRefetchSubmission(prev => !prev);
      return submission;
    } catch (error) {
      console.error(`Error pushing submission ${submission.id || ""}`, error);
    }
  };


  return {
    submission,
    ...submission,
    appendFile: (file: any) => setSubmissionFiles((prev: any) => [...prev, file]),
    approve: approveSubmission,
    approveSubmission,
    deleteSubmission,
    fetched,
    isPending,
    push: pushSubmission,
    pushSubmission,
    refetch: fetchSubmission,
    reject: rejectSubmission,
    rejectSubmission,
    reset: setDefaults,
    setDescription: (description: string) => setSubmission((prev: any) => ({...prev, description})),
    setFiles: (files: any) => setSubmissionFiles(files),
    setId,
    setName: (name: string) => setSubmission((prev: any) => ({...prev, name})),
    setPostTitle: (postTitle: string) => setSubmission((prev: any) => ({...prev, postTitle})),
    setSchemaId: (schemaId: string) => setSubmission((prev: any) => ({...prev, schemaId})),
    setState: (state: string) => setSubmission((prev: any) => ({...prev, state})),
    uploadPendingAfterTimeout
  };
};

// params is object or null
export const useSubmissions = (submissionIds: string[] = [], params: { [key: string]: any } = {})
  : UseSubmissionsValue => {
  const {getSubmissions, getSubmissionsByIds, updateSubmission, updateSubmissionsStates,
    deleteSubmission: deleteSubmissionFromApi} = useApi();
  const {createApproveComment, createRejectComment} = useComment();
  const {PROJECT_ID} = usePlayMakersConfig();

  const [submissionStates, setSubmissionStates] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [refetchSubmissions, setRefetchSubmissions] = useState<number>(0);
  const [fetched, setFetched] = useState<boolean>(false);

  const {appendNextPage, refreshLastPage, checkHasMore, currentPage} =
      usePagination<any>(setSubmissionStates, setHasMore, hasMore, params?.page, params?.limit);

  // fetch by ids list, or by params
  const fetchSubmissions = async(appendParams: {[key:string]: any} = {}) => {
    let submissions: any = [];

    const validKeys = ["schemaId", "state", "ownerId", "limit", "page"];
    // if any term is passed as undefined, return empty array
    for(const key of validKeys) {
      if(Object.prototype.hasOwnProperty.call(params, key) &&
        (params[key] === undefined || params[key] === "")) return [];
    }

    const fixedParams = {...params, ...appendParams};

    if(Object.keys(fixedParams).length > 0) {
      if(PROJECT_ID) fixedParams.projectId = PROJECT_ID;
      submissions = await withCache(getSubmissions, [fixedParams], 25_000);

    } else if(submissionIds.length > 0)
      submissions = await withCache(getSubmissionsByIds, [submissionIds], 60_000);

    return submissions;
  };

  const fetchAndAugmentSubmissions = async(quiet: boolean = false) => {
    if(!quiet) setFetched(false);
    const fetchedSubmissions = await fetchSubmissions();
    const submissionWithStates = fetchedSubmissions.map((submission:any) => ({
      submission,
      submissionState: {}
    }));

    setSubmissionStates(checkHasMore(submissionWithStates));
    setFetched(true);
  };

  const fetchMore = async(refresh?: boolean) => {
    try {
      setFetched(false);
      if(refresh) {
        clearCache("getSubmissions");
        clearCache("getSubmissionsByIds");
        await refreshLastPage(d => fetchSubmissions(d),
          (submission:any) => ({submission, submissionState: {}})
        );
      } else {
        await appendNextPage(
          d => fetchSubmissions(d),
          (submission:any) => ({submission, submissionState: {}})
        );
      }
    } catch (error) {
      console.error("Error fetching more schemas", error);
      throw new Error("Error fetching more schemas", {cause: error});
    } finally {
      setFetched(true);
    }
  };


  // Refetch on manual refetch,
  useEffect(() => {
    if(refetchSubmissions) {
      clearCache("getSubmissions");
      clearCache("getSubmissionsByIds");
      fetchAndAugmentSubmissions();
    }
  }, [refetchSubmissions]
  );
  // Refetch on parameters change, submissionIds change (including order)
  useEffect(() => {
    fetchAndAugmentSubmissions();
  }, [
    JSON.stringify(params),
    JSON.stringify(submissionIds),
    PROJECT_ID
  ]);

  const updateSubmissionState = (submissionId: string, updates: any) => {
    setSubmissionStates(prevStates =>
      prevStates.map(state =>
        state.submission.id === submissionId
          ? {...state, submission: {...state.submission, ...updates}}
          : state
      )
    );
  };

  const updateAllSubmissionsStates = (updates: any) => {
    setSubmissionStates(prevStates =>
      prevStates.map(state => ({
        ...state,
        submission: {
          ...state.submission,
          ...updates
        }
      }))
    );
  };

  const pushSubmission = async(
    submission: any, submissionUpdates: any = null) => {

    if(submissionUpdates)
      submission = {...submission, ...submissionUpdates};

    if(!submission) {
      console.error("No submission to push");
      return;
    }

    if(!submission.name || !submission.schemaId || !submission.state) {
      console.error(`Missing required parameters for ${submission.id || ""} submission. ` +
      `${submission.name ? "" : "Name is missing. "}` +
      `${submission.schemaId ? "" : "schemaId is missing. "}` +
      `${submission.state ? "" : "State is missing. "}`);
      return;
    }

    const params: any = {
      description: submission.description || "",
      name: submission.name,
      postTitle: submission.postTitle,
      schemaId: submission.schemaId,
      state: submission.state
    };

    try {
      submission = await updateSubmission(submission.id, params);
      updateSubmissionState(submission.id, submission);
      return submission;
    } catch (error) {
      console.error(`Error fetching submission ${submission.id}`, error);
    }
  };

  const pushState = async(submission: any, state: string, commentBody?: string) => {
    const previousSubmission = {...submission};
    try {
      updateSubmissionState(submission.id, {state});
      const updatedSubmission = await pushSubmission(submission, {state});
      if(updatedSubmission && commentBody !== undefined) {
        if(state === "approved")
          await createApproveComment(submission.id, commentBody);
        else if(state === "rejected")
          await createRejectComment(submission.id, commentBody);
      }
      setSubmissionStates(prevStates =>
        prevStates.filter(state => state.submission.id !== submission.id)
      );
    } catch (error) {
      console.error(`Error updating submission ${submission.id}`, error);
      updateSubmissionState(submission.id, previousSubmission);
    }
  };

  const approveAll = async() => {
    updateAllSubmissionsStates({state: "approved"});
    const ids = submissionStates.map((state: any) => state.submission.id);
    await updateSubmissionsStates(ids, "approved");
    setRefetchSubmissions(prev => prev++);
  };

  const rejectAll = async() => {
    updateAllSubmissionsStates({state: "rejected"});
    const ids = submissionStates.map((state: any) => state.submission.id);
    await updateSubmissionsStates(ids, "rejected");
    setRefetchSubmissions(prev => prev++);
  };

  const deleteSubmission = async(submission: any ) => {
    try {
      await deleteSubmissionFromApi(submission.id);
      const updatedSubmissionStates = submissionStates.filter(state => state.submission.id !== submission.id);
      setSubmissionStates(updatedSubmissionStates);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    approveAll,
    currentPage,
    fetchMore,
    fetched,
    hasMore,
    refresh: fetchAndAugmentSubmissions,
    rejectAll,
    submissions: submissionStates.map(state => ({
      ...state.submission,
      approve: (comment?:string) => pushState(state.submission, "approved", comment),
      delete: () => deleteSubmission(state.submission),
      push: (updates: any) => pushSubmission(state.submission, updates),
      pushState: (submissionState:string, comment?:string) => pushState(state.submission, submissionState, comment),
      reject: (comment?:string) => pushState(state.submission, "rejected", comment),
      setDescription: (description: string) => updateSubmissionState(state.submission.id, {description}),
      setName: (name: string) => updateSubmissionState(state.submission.id, {name}),
      setSchemaId: (schemaId: string) => updateSubmissionState(state.submission.id, {schemaId}),
      setState: (submissionState: string) => updateSubmissionState(state.submission.id, {submissionState})
    }))
  };
};

// Utlity functions to fetch submissions by different parameters
export const useSubmissionsByParams = (params: SubmissionParams) => useSubmissions([], params);
export const useSubmissionsByState = (state: string, params?: SubmissionParams) => {
  const {submissions, ...useSubmissionsData} = useSubmissions([], {state, ...params});
  return {
    submissions: submissions.filter(submission => submission.state === state),
    ...useSubmissionsData
  };
};
export const useValidatedSubmissions =
  (params?: SubmissionParams) => useSubmissions([], {state: "submitted", ...params});
export const useSubmissionsBySchema =
  (schemaId: string, params?: SubmissionParams) => useSubmissions([], {schemaId, ...params});
export const useSubmissionsByOwner =
  (ownerId: string, params?: SubmissionParams) => useSubmissions([], {ownerId, ...params});
